import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  assignRandomFault,
  assignFaultsToTiers as assignFaultsToTiersUtil,
  assignFaultToTier as assignFaultToTierUtil,
  assignFaultsToComponentsUtil,
  getFaultAssignments,
  clearAllFaultAssignments,
} from "../utils/faults";

const FaultContext = createContext();

export const useFaults = () => {
  const context = useContext(FaultContext);
  if (!context) {
    throw new Error("useFaults must be used within a FaultProvider");
  }
  return context;
};

export const FaultProvider = ({ children }) => {
  const [faultMap, setFaultMap] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Hydrate React state once from the module cache on mount.
  // All subsequent reads go through the module cache directly —
  // no per-render localStorage hits.
  useEffect(() => {
    setFaultMap(getFaultAssignments());
    setIsInitialized(true);
  }, []);

  // ─── Tiers ──────────────────────────────────────────────────────────────────

  const getFaultForTier = useCallback((tierId) => {
    if (!tierId) return null;
    // Read straight from the module cache — always fresh, no stale closure risk
    return getFaultAssignments()[tierId] ?? null;
  }, []);

  /**
   * Bulk-assigns faults to an array of tier objects.
   * The heavy work (cache lookup + debounced persist) lives in faults.js.
   * We do a single setState after so React consumers stay in sync.
   */
  const assignFaultsToTiers = useCallback((tiers) => {
    if (!tiers || tiers.length === 0) return [];
    const result = assignFaultsToTiersUtil(tiers);
    // Only sync state if new entries were added
    const freshMap = getFaultAssignments();
    setFaultMap((prev) => {
      const hasNew = result.some(
        (t) => !Object.prototype.hasOwnProperty.call(prev, t.id),
      );
      return hasNew ? { ...freshMap } : prev;
    });
    return result;
  }, []);

  /**
   * Assigns a fault to a single tier.
   * Returns the fault (null = healthy).
   */
  const assignFaultToTier = useCallback((tierId) => {
    if (!tierId) return null;
    const fault = assignFaultToTierUtil(tierId);
    // Only trigger a re-render if this tier was newly assigned
    setFaultMap((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, tierId)) return prev;
      return { ...prev, [tierId]: fault };
    });
    return fault;
  }, []);

  // ─── Components ─────────────────────────────────────────────────────────────

  /**
   * Assigns faults to components with parent-severity inheritance.
   * No React state needed — components receive faults as props via the
   * returned array, not via context subscription.
   */
  const assignFaultsToComponents = useCallback((components, parentSeverity) => {
    return assignFaultsToComponentsUtil(components, parentSeverity);
  }, []);

  // ─── Mutations ───────────────────────────────────────────────────────────────

  const updateFaultForTier = useCallback((tierId, fault) => {
    setFaultMap((prev) => ({ ...prev, [tierId]: fault }));
  }, []);

  const removeFaultForTier = useCallback((tierId) => {
    setFaultMap((prev) => {
      const next = { ...prev };
      delete next[tierId];
      return next;
    });
  }, []);

  const clearAllFaults = useCallback(() => {
    clearAllFaultAssignments();
    setFaultMap({});
  }, []);

  const regenerateAllFaults = useCallback((tiers) => {
    if (!tiers || tiers.length === 0) return;
    const newMap = {};
    tiers.forEach((tier) => {
      newMap[tier.id] = assignRandomFault();
    });
    // Write to module cache so persistence stays consistent
    Object.entries(newMap).forEach(([id, fault]) => {
      getFaultAssignments()[id] = fault;
    });
    setFaultMap(newMap);
  }, []);

  // ─── Context value ───────────────────────────────────────────────────────────

  const value = {
    faultMap,
    isInitialized,
    getFaultForTier,
    assignFaultToTier,
    assignFaultsToTiers,
    assignFaultsToComponents,
    updateFaultForTier,
    removeFaultForTier,
    clearAllFaults,
    regenerateAllFaults,
  };

  return (
    <FaultContext.Provider value={value}>{children}</FaultContext.Provider>
  );
};
